// Dr Cleanstein's answers, read for a phone. The same small parser as the Portal's
// AskAnswer (apps/portal/src/components/ask/AskAnswer.tsx): paragraphs, bullet and
// numbered lists, headings, **bold**, *italic* and tables. Copied, not imported, so
// the two apps build on their own. Tables become cards on a phone (see DrcAnswer).

export interface AnswerTable {
  headers: string[];
  rows: string[][];
}

export type Block =
  | { kind: 'p'; lines: string[] }
  // subs: indented bullets under each item, the same length as items (Gary, 1 Nov: nested steps read as one list).
  | { kind: 'ul'; items: string[]; subs: string[][] }
  | { kind: 'ol'; items: string[]; subs: string[][]; start: number }
  | { kind: 'h'; text: string }
  | { kind: 'table'; table: AnswerTable };

const TABLE_RULE = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

export function parseAnswer(text: string): Block[] {
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = (lines[i] ?? '').trim();
    if (!trimmed) { i++; continue; }

    if (trimmed.startsWith('|') && TABLE_RULE.test((lines[i + 1] ?? '').trim())) {
      const headers = splitRow(trimmed);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push(splitRow(lines[i] ?? ''));
        i++;
      }
      blocks.push({ kind: 'table', table: { headers, rows } });
      continue;
    }

    const heading = trimmed.match(/^#{1,6}\s+(.*)$/);
    if (heading) { blocks.push({ kind: 'h', text: heading[1] ?? '' }); i++; continue; }

    if (/^[-*•]\s+/.test(trimmed) || /^\d+[.)]\s+/.test(trimmed)) {
      const ordered = /^\d+[.)]\s+/.test(trimmed);
      const start = ordered ? Number(trimmed.match(/^(\d+)/)?.[1] ?? '1') : 1;
      const items: string[] = [];
      const subs: string[][] = [];
      while (i < lines.length) {
        const raw = lines[i] ?? '';
        const t = raw.trim();
        // A blank line between items, or before an indented bullet, keeps the list going.
        if (!t) {
          const next = lines.slice(i + 1).find((l) => l.trim());
          const nt = (next ?? '').trim();
          const continues = !!next && ((/^\s{2,}|^\t/.test(next) && /^[-*•]\s+/.test(nt)) || (ordered ? /^\d+[.)]\s+/.test(nt) : /^[-*•]\s+/.test(nt)));
          if (!continues) break;
          i++;
          continue;
        }
        const indented = /^\s{2,}|^\t/.test(raw);
        const sub = t.match(/^[-*•]\s+(.*)$/);
        if (indented && sub && items.length) {
          subs[items.length - 1]!.push(sub[1] ?? '');
          i++;
          continue;
        }
        const m = ordered ? t.match(/^\d+[.)]\s+(.*)$/) : t.match(/^[-*•]\s+(.*)$/);
        if (!m) break;
        items.push(m[1] ?? '');
        subs.push([]);
        i++;
      }
      blocks.push(ordered ? { kind: 'ol', items, subs, start } : { kind: 'ul', items, subs });
      continue;
    }

    const para: string[] = [];
    while (i < lines.length) {
      const t = (lines[i] ?? '').trim();
      if (!t || t.startsWith('|') || /^#{1,6}\s/.test(t) || /^[-*•]\s+/.test(t) || /^\d+[.)]\s+/.test(t)) break;
      para.push(t);
      i++;
    }
    if (para.length === 0) { para.push(trimmed); i++; }
    blocks.push({ kind: 'p', lines: para });
  }
  return blocks;
}

/** Inline markers removed, for plain-text uses ("Use in reply"). */
export function plainCell(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|\s)\*(\S.*?\S|\S)\*(?=\s|$)/g, '$1$2')
    .replace(/`/g, '');
}

/** One table row as a line of text: "Title: Label value, Label value". */
export function rowAsLine(headers: string[], row: string[]): string {
  const title = plainCell(row[0] ?? '');
  const rest = row.slice(1)
    .map((cell, k) => ({ label: plainCell(headers[k + 1] ?? ''), value: plainCell(cell) }))
    .filter((c) => c.value)
    .map((c) => (c.label ? `${c.label} ${c.value}` : c.value));
  return rest.length ? `${title}: ${rest.join(', ')}` : title;
}

/** The answer as plain text for a WhatsApp reply: no markdown, and no table pipes. */
export function plainAnswer(text: string): string {
  return parseAnswer(text).map((b) => {
    if (b.kind === 'h') return plainCell(b.text);
    const subLines = (k: number) => (b.kind === 'ul' || b.kind === 'ol' ? (b.subs[k] ?? []).map((s) => `\n   - ${plainCell(s)}`).join('') : '');
    if (b.kind === 'ul') return b.items.map((it, k) => `• ${plainCell(it)}${subLines(k)}`).join('\n');
    if (b.kind === 'ol') return b.items.map((it, n) => `${b.start + n}. ${plainCell(it)}${subLines(n)}`).join('\n');
    if (b.kind === 'table') return b.table.rows.map((r) => rowAsLine(b.table.headers, r)).join('\n');
    return b.lines.map(plainCell).join('\n');
  }).join('\n\n').trim();
}

/** Inline pieces: bold, italic, plain text and the only links allowed (ours). */
export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'link'; text: string; href: string };

/**
 * Only https links on cleanmybed.com (and its subdomains, Sentinel's own sites) become
 * tappable. Anything else stays plain text: answers can quote text the public typed, so
 * a link elsewhere could carry data out or send an operator to a fake page.
 */
export function safeHref(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return null;
    const host = u.hostname.toLowerCase();
    return host === 'cleanmybed.com' || host.endsWith('.cleanmybed.com') ? u.toString() : null;
  } catch {
    return null;
  }
}

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const re = /(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(https:\/\/[^\s)]+)/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index).replace(/`/g, '') });
    const [whole] = m;
    if (m[1]) out.push({ kind: 'bold', text: whole.slice(2, -2) });
    else if (m[2]) out.push({ kind: 'italic', text: whole.slice(1, -1) });
    else {
      const href = safeHref(whole);
      out.push(href ? { kind: 'link', text: whole, href } : { kind: 'text', text: whole });
    }
    last = m.index + whole.length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last).replace(/`/g, '') });
  return out;
}
